
import React, { useState } from 'react';
import { confirmPasswordReset } from '../services/apiService';
import AmplifierIcon from './icons/AmplifierIcon';

interface ResetPasswordPageProps {
    token: string;
    handle: string;
    onDone: () => void; // called after success or "go to login"
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ token, handle, onDone }) => {
    const [newPassword, setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading]         = useState(false);
    const [error, setError]                 = useState('');
    const [done, setDone]                   = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            await confirmPasswordReset(token, handle, newPassword);
            setDone(true);
        } catch (err: any) {
            if (err.message?.includes('expired')) {
                setError('This reset link has expired. Please request a new one.');
            } else {
                setError(err.message || 'Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-brand-dark min-h-screen flex items-center justify-center p-4 font-urbanist">
            <div className="w-full max-w-md bg-brand-surface rounded-xl shadow-lg p-8 border border-gray-800">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center text-brand-orange mb-2">
                        <AmplifierIcon className="h-10 w-10" />
                        <h1 className="text-3xl font-bold tracking-tight ml-2 text-white">beYourAMP</h1>
                    </div>

                    {done ? (
                        <>
                            <div className="text-5xl mt-4 mb-2">🎉</div>
                            <h2 className="text-xl font-black text-white uppercase tracking-tight">Password Updated!</h2>
                            <p className="text-brand-muted mt-2 text-sm">
                                Your password for <strong className="text-white">@{handle}</strong> has been changed successfully.
                            </p>
                            <button
                                onClick={onDone}
                                className="mt-6 w-full bg-brand-orange text-white font-black py-3 px-4 rounded-full hover:opacity-90 transition-opacity uppercase tracking-widest text-sm shadow-lg shadow-brand-orange/20"
                            >
                                Go to Sign In →
                            </button>
                        </>
                    ) : (
                        <>
                            <h2 className="text-xl font-bold text-white mt-4">Set New Password</h2>
                            <p className="text-brand-muted mt-2 text-sm">
                                Choose a new password for <strong className="text-white">@{handle}</strong>
                            </p>

                            <form onSubmit={handleSubmit} className="mt-6 space-y-4 text-left">
                                <div>
                                    <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">New Password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        placeholder="Min. 8 characters"
                                        required
                                        minLength={8}
                                        className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-brand-muted mb-1 tracking-widest uppercase">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        placeholder="Repeat password"
                                        required
                                        className="w-full bg-gray-700 text-white rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-brand-orange font-bold"
                                    />
                                </div>

                                {error && (
                                    <p className="text-red-400 text-sm font-bold text-center">{error}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoading || !newPassword || !confirmPassword}
                                    className="w-full bg-brand-orange text-white font-black py-3 px-4 rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity uppercase tracking-widest text-sm shadow-lg shadow-brand-orange/20"
                                >
                                    {isLoading
                                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                                        : 'Set New Password'}
                                </button>
                            </form>

                            <div className="text-center mt-6 pt-4 border-t border-gray-800">
                                <button
                                    onClick={onDone}
                                    className="text-xs font-bold text-brand-muted hover:text-white transition-colors uppercase tracking-widest"
                                >
                                    ← Back to Sign In
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
